import { useNavigate } from "react-router-dom";
import { Button, EmptyState, PageHead, PersonCell, Skeleton, StatusPill } from "@guestpilot/ui-kit";
import { useGet } from "../../api/hooks";
import type { Schema, Team } from "../../api/types";
import { formatDate } from "../../format";
import { useI18n } from "../../i18n";
import { useSession } from "../../session";

type Removal = Schema<"RemovalRequest">;
type Exception = Schema<"ExceptionRecord">;

/** Read-only follow-up: what the owner already submitted and what needs a second look (team-tab.md Pending). */
export function Pending({ team }: { team: Team }) {
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const tz = s.me.tenant.timeZone;
  const removals = useGet<Removal[]>(
    "/api/v1/removals",
    { teamId: team.id, status: "pending" },
    { enabled: s.can("REMOVAL_READ", team.id) },
  );
  const exceptions = useGet<Exception[]>(
    "/api/v1/exceptions",
    { teamId: team.id, status: "expired" },
    { enabled: s.can("EXCEPTION_READ", team.id) },
  );
  if (removals.isLoading || exceptions.isLoading) return <Skeleton rows={4} />;
  const r = removals.data ?? [];
  const e = exceptions.data ?? [];
  return (
    <div className="gp-page gp-page--narrow">
      <PageHead title={t("ui:pending.title")} subtitle={t("ui:pending.lead")} />
      {r.length === 0 && e.length === 0 ? <EmptyState title={t("ui:pending.empty")} /> : null}
      {r.length > 0 ? (
        <section aria-labelledby="pr-h" className="gp-stack">
          <h2 id="pr-h" className="t-title-sm" style={{ margin: 0 }}>
            {t("ui:pending.removals", { n: r.length })}
          </h2>
          {r.map((x) => (
            <div key={x.id} className="gp-card">
              <div className="gp-row" style={{ justifyContent: "space-between" }}>
                <PersonCell
                  name={x.participant?.displayName || x.participant?.email || ""}
                  sub={x.participant?.displayName ? x.participant.email : undefined}
                />
                <StatusPill
                  domain="removal"
                  value={x.status ?? "pending"}
                  label={t(`ui:removalStatus.${x.status}`)}
                />
              </div>
              <p className="t-body gp-muted" style={{ margin: 0 }}>
                {x.status === "scheduled" && x.executeAt
                  ? t("ui:pending.scheduled", { date: formatDate(x.executeAt, locale, tz) })
                  : t("ui:pending.waiting")}
              </p>
            </div>
          ))}
        </section>
      ) : null}
      {e.length > 0 ? (
        <section aria-labelledby="pe-h" className="gp-stack">
          <h2 id="pe-h" className="t-title-sm" style={{ margin: 0 }}>
            {t("ui:pending.exceptions", { n: e.length })}
          </h2>
          {e.map((x) => (
            <div key={x.id} className="gp-card">
              <div className="gp-row" style={{ justifyContent: "space-between" }}>
                <PersonCell
                  name={x.participant?.displayName || x.participant?.email || ""}
                  sub={x.participant?.displayName ? x.participant.email : undefined}
                />
                <StatusPill domain="exception" value="expired" label={t("ui:pending.needsReview")} />
              </div>
              <p className="t-body gp-muted" style={{ margin: 0 }}>
                {t("ui:pending.expiredOn", { date: formatDate(x.validUntil, locale, tz) })}
              </p>
              {team.activeCampaignId ? (
                <Button onClick={() => nav(`/review/${team.activeCampaignId}`)}>
                  {t("ui:pending.review")}
                </Button>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
